import { supabase } from '@/integrations/supabase/client';

export interface CoinData {
  symbol: string;
  name: string;
  price: number;
  market_cap: number;
  volume_24h: number;
  change_24h: number;
  change_7d: number;
  sparkline: number[];
  categories: string[];
}

export async function fetchLiveMarketData(): Promise<CoinData[]> {
  const { data, error } = await supabase.functions.invoke('fetch-scored-markets');

  if (error) {
    throw new Error(`Failed to fetch market data: ${error.message}`);
  }

  const tokens = data?.tokens || [];

  return tokens.map((coin: any) => ({
    ...coin,
    categories: categorizeToken(coin.symbol.toLowerCase()),
  }));
}

function categorizeToken(symbol: string): string[] {
  const categories: string[] = [];
  
  // DeFi
  if (['uni', 'aave', 'comp', 'mkr', 'crv', 'sushi', 'snx'].includes(symbol)) {
    categories.push('defi');
  }
  
  // Layer 1
  if (['eth', 'sol', 'ada', 'avax', 'dot', 'near', 'ftm', 'algo', 'atom'].includes(symbol)) {
    categories.push('layer1');
  }
  
  // Layer 2
  if (['matic', 'arb', 'op', 'imx', 'lrc'].includes(symbol)) {
    categories.push('layer2');
  }
  
  // AI
  if (['fet', 'agix', 'ocean', 'rndr', 'grt'].includes(symbol)) {
    categories.push('ai');
  }
  
  // Gaming
  if (['sand', 'mana', 'axs', 'gala', 'enj', 'imx', 'ron'].includes(symbol)) {
    categories.push('gaming');
  }
  
  // Meme
  if (['doge', 'shib', 'pepe', 'floki', 'bonk'].includes(symbol)) {
    categories.push('meme');
  }
  
  return categories.length > 0 ? categories : ['other'];
}

export interface ScoredCoinData extends CoinData {
  score: number;
  momentum: number;
  volume: number;
  trend: number;
}

export function scoreTokenOpportunities(marketData: CoinData[]): ScoredCoinData[] {
  return marketData.map(token => {
    // Score based on price momentum (higher recent gains = higher score)
    const momentumScore = Math.max(0, Math.min(100, (token.change_24h * 2) + 50));
    
    // Score based on volume/market cap ratio
    const volumeRatio = token.volume_24h / token.market_cap;
    const volumeScore = Math.min(100, volumeRatio * 100);
    
    // Score based on 7-day trend
    const trendScore = Math.max(0, Math.min(100, (token.change_7d * 1.5) + 50));
    
    // Composite score (weighted average)
    const totalScore = Math.round(
      momentumScore * 0.4 + 
      volumeScore * 0.3 + 
      trendScore * 0.3
    );
    
    return {
      ...token,
      score: totalScore,
      momentum: Math.round(momentumScore),
      volume: Math.round(volumeScore),
      trend: Math.round(trendScore)
    };
  }).sort((a, b) => b.score - a.score);
}

export function getTopByCategory(scoredTokens: ScoredCoinData[], category: string, limit = 5): ScoredCoinData[] {
  return scoredTokens
    .filter(t => t.categories.includes(category))
    .slice(0, limit);
}
